import React, { useState } from "react";
import { IonButton, IonIcon } from "@ionic/react";
import { chevronDown, chevronUp } from "ionicons/icons";
import styles from "./CollapsibleEntityList.module.scss";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";

interface CollapsibleEntityListProps<T> {
  entities: T[];
  isLoading: boolean;
  renderItem: (entity: T) => React.ReactNode;
  renderLoading: () => React.ReactNode;
  renderEmpty: () => React.ReactNode;
  initialVisibleCount?: number;
  loadingMessage?: string;
  isDeleting?: boolean;
  renderShowMore?: (props: { showAll: boolean; setShowAll: (value: boolean) => void }) => React.ReactNode;
  containerClassName?: string;
}

const cx = classNames.bind(styles);

function CollapsibleEntityList<T>({
  entities,
  isLoading,
  renderItem,
  renderLoading,
  renderEmpty,
  containerClassName,
  initialVisibleCount = 2,
  isDeleting = false,
  renderShowMore,
}: CollapsibleEntityListProps<T>) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return <>{renderLoading()}</>;
  }

  if (entities.length === 0) {
    return <>{renderEmpty()}</>;
  }

  const visibleEntities = showAll ? entities : entities.slice(0, initialVisibleCount);

  const renderDefaultShowMore = () => {
    return (
      <IonButton
        expand="block"
        fill="clear"
        onClick={() => setShowAll(!showAll)}
        className={cx("collapsibleButton")}
      >
        <span className={cx("collapsibleButtonText")}>{showAll ? t("Show less") : t("Show more")}</span>
        <IonIcon icon={showAll ? chevronUp : chevronDown} />
      </IonButton>
    );
  };

  return (
    <div className={styles.container}>
      {isDeleting && (
        <div className={cx("deleteWaitingOverlay")}>
          <div
            className={cx("deleteWaitingOverlayText", visibleEntities.length === 1 && "deleteWaitingOverlayTextSingle")}
          >
            {t("Please wait...")}
          </div>
        </div>
      )}
      {visibleEntities.map((entity, index) => (
        <React.Fragment key={`entity-${index}-${JSON.stringify(entity)}`}>{renderItem(entity)}</React.Fragment>
      ))}
      {entities.length > initialVisibleCount && (renderShowMore?.({ showAll, setShowAll }) || renderDefaultShowMore())}
    </div>
  );
}

export default CollapsibleEntityList;
